import React, { useState } from 'react';
import { loginUser } from '../../libs/api';
import { AES } from 'crypto-js'; // 使用 AES 进行加密
import Cookies from 'js-cookie'; // 引入 js-cookie
import Button from '../common/Button'; // 引入通用按钮组件
import GoogleSignIn from './GoogleSignIn'; // 引入Google登录组件
import styles from '@/components/Auth/Auth.module.less'; // 使用 styles 导入
import { useLocation } from 'react-router-dom';
import ThreeBackground from './ThreeBackground'; // 引入Three.js背景组件
import { Checkbox, Input, Form } from 'antd';
import { normalizeAccessToken } from '@/utils/authToken';

interface LocationState {
  from: {
    pathname: string;
  };
}

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const from = (location.state as LocationState)?.from?.pathname || '/';

  const onFinish = async (values: { email: string; password: string; agreed: boolean }) => {
    // 确保用户已同意隐私协议
    if (!values.agreed) {
      setError('请先阅读并同意隐私协议与服务条款');
      return;
    }

    setError(null);
    setLoading(true);

    // 设置50秒超时
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError('登录超时，请重新尝试。');
    }, 50000);

    const secretKey = process.env.REACT_APP_AES_SECRET_KEY;
    if (!secretKey) {
      console.error('AES_SECRET_KEY is not defined in environment variables.');
      setError('客户端配置错误。');
      setLoading(false);
      clearTimeout(timeoutId);
      return;
    }
    const encryptedPassword = AES.encrypt(values.password, secretKey).toString();

    try {
      const result = await loginUser({ email: values.email, password: encryptedPassword });
      console.log('登录成功:', result);

      clearTimeout(timeoutId);

      // 设置认证数据
      const accessToken = normalizeAccessToken(result.accessToken);
      if (accessToken) {
        Cookies.set('accessToken', accessToken, { expires: 7 });
      }
      Cookies.set('userId', result.userId, { expires: 7 });
      localStorage.setItem('userInfo', JSON.stringify(result));

      // 获取重定向路径
      const redirectPath = sessionStorage.getItem('redirectPath') || from || '/';
      sessionStorage.removeItem('redirectPath');

      window.location.href = redirectPath;
    } catch (error) {
      console.error('登录失败:', error);
      setError('登录失败，请检查您的凭据。');
      setLoading(false);
      clearTimeout(timeoutId);
    }
  };

  return (
    <div className={styles['auth-page']}>
      <ThreeBackground />
      <div className={styles['auth-container']}>
        <Form form={form} onFinish={onFinish} className={styles['auth-form']} layout="vertical">
          <h2>登录</h2>
          <Form.Item
            style={{ width: '100%' }}
            name="email"
            rules={[
              { required: true, message: '请输入电子邮件' },
              { type: 'email', message: '请输入有效的电子邮件地址' },
            ]}
          >
            <Input type="email" placeholder="电子邮件" size="large" />
          </Form.Item>
          <Form.Item
            style={{ width: '100%' }}
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少需要6个字符' },
            ]}
          >
            <Input.Password placeholder="密码" size="large" />
          </Form.Item>
          {error && <div className={styles['error-message']}>{error}</div>}
          <Form.Item
            style={{ width: '100%' }}
            name="agreed"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error('请先阅读并同意隐私协议与服务条款')),
              },
            ]}
          >
            <Checkbox>
              阅读并同意
              <a target="_blank" href="/privacy-policy">
                隐私声明
              </a>
              与
              <a target="_blank" href="/terms-of-service">
                服务条款
              </a>
            </Checkbox>
          </Form.Item>
          <Form.Item shouldUpdate style={{ width: '100%' }}>
            {() => {
              const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
              const allFieldsFilled =
                form.getFieldValue('email') &&
                form.getFieldValue('password') &&
                form.getFieldValue('agreed');

              return (
                <Button
                  type="submit"
                  disabled={loading || !allFieldsFilled || hasErrors}
                  className="primaryButton"
                >
                  {loading ? '登录中...' : '登 录'}
                </Button>
              );
            }}
          </Form.Item>
          <div className={styles['auth-links']}>
            <a href="/register" className={styles['register-link']}>
              注册新账号
            </a>
            <a href="/forgot-password" className={styles['forgot-password-link']}>
              忘记密码？
            </a>
          </div>
          {/* 第三方登录按钮 */}
          <div className={styles['oauth-buttons']}>
            <GoogleSignIn
              className={styles['google-signin']}
              onError={() => setError('Google登录失败，请重试')}
            />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default Login;
